import { useEffect, useState } from 'react'

import type { LedgerJournalEntryDetail, PostedJournalResult } from '@/api/ledger-types'
import { ProblemAlert } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { formatBusinessDate } from '@/lib/datetime'

import { LedgerDualControlNotice, LedgerDualControlOutcome } from './dual-control'
import { useReverseJournal } from './mutations'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'

/**
 * Reversing a posted entry.
 *
 * The ledger's only correction, and a countersigned one. Three things are said before the click,
 * because each of them changes what the operator is agreeing to:
 *
 * - **Both entries stay in the book.** A reversal does not remove the original; it adds a mirror of
 *   it. An auditor reads the pair, and the reason travels on both.
 * - **It posts into the current open day, not into the original's day.** Reversing a Friday posting
 *   on Monday puts the correcting entry in Monday. That is correct accounting and it surprises
 *   people, so it is stated rather than discovered when the trial balance for Friday does not move.
 * - **Confirming may only queue it.** `ledger.journal.reverse.approve` decides whether this
 *   completes now or waits, and the answer says which actually happened rather than assuming.
 *
 * A dialog rather than a screen of its own: no amount is keyed here — the amounts are the original
 * entry's, mirrored — so the mobile policy's refusal case does not apply. The reason is the only
 * input, and it is mandatory.
 */
export function ReverseJournalDialog({
  entry,
  open,
  onOpenChange,
  onPosted,
}: {
  entry: LedgerJournalEntryDetail
  open: boolean
  onOpenChange: (open: boolean) => void
  onPosted?: (entryNumber: string | null) => void
}) {
  const permissions = useLedgerPermissions()
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<PostedJournalResult | null>(null)

  const reverse = useReverseJournal()

  // The dialog is remounted rarely and reopened often, so a closed dialog clears itself: reopening
  // it on a different entry must never show the previous entry's reason or its outcome.
  useEffect(() => {
    if (!open) {
      setReason('')
      setResult(null)
    }
  }, [open])

  const close = (next: boolean) => {
    if (!next) {
      reverse.reset()
    }
    onOpenChange(next)
  }

  const trimmed = reason.trim()

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reverse {entry.entryNumber}</DialogTitle>
          <DialogDescription>
            {entry.description} — posted on {formatBusinessDate(entry.businessDate)}.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <div className="rounded-control border border-border-subtle bg-surface-muted p-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-xs text-muted">Total to be mirrored</span>
              <MoneyDisplay
                value={entry.totalDebitMinorUnits}
                currency={entry.currencyCode}
                emphasis="strong"
              />
            </div>
            <p className="mt-1 text-2xs text-muted">
              {entry.lines.length} line{entry.lines.length === 1 ? '' : 's'}, each posted on the
              opposite side. The original entry is untouched and stays in the book.
            </p>
          </div>

          {result === null ? (
            <>
              <LedgerDualControlNotice
                action="Reversing a posted entry"
                approvePermission={LEDGER_PERMISSIONS.journalReverseApprove}
                canCountersign={permissions.canCountersignReversal}
                expiry="A ledger journal request expires twelve hours after it is made — half the institution's usual window — so one raised late in the afternoon lapses before the next working afternoon."
              />

              <p className="text-xs text-body">
                The reversal posts into the branch&apos;s <strong>current open day</strong>, not into{' '}
                {formatBusinessDate(entry.businessDate)}. Reversing an older posting today moves
                today&apos;s figures, which is what correcting an already-reported day looks like.
              </p>

              <div className="space-y-1.5">
                <Label htmlFor="reversal-reason">Why is this being reversed?</Label>
                <textarea
                  id="reversal-reason"
                  rows={3}
                  autoFocus
                  value={reason}
                  placeholder="What was wrong with the original posting."
                  onChange={(event) => setReason(event.target.value)}
                  aria-describedby="reversal-reason-hint"
                  className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading outline-none transition-colors placeholder:text-muted focus:border-brand-text hover:border-border-strong"
                />
                <p id="reversal-reason-hint" className="text-xs text-muted">
                  Mandatory, and stored on both entries. It is what a checker reads before signing
                  and what an auditor reads afterwards.
                </p>
              </div>

              {reverse.isError ? <ProblemAlert error={reverse.error} /> : null}
            </>
          ) : (
            <LedgerDualControlOutcome
              result={result}
              awaitingDescription={
                <>
                  The reversal of {entry.entryNumber} is stored and waiting for a countersignature.{' '}
                  <strong>The original entry still stands</strong> and nothing has moved.
                </>
              }
              appliedDescription={
                <>
                  {entry.entryNumber} has been reversed
                  {result.entryNumber ? (
                    <>
                      {' '}
                      by <span className="font-mono text-xs">{result.entryNumber}</span>
                    </>
                  ) : null}
                  {result.businessDate ? (
                    <> on {formatBusinessDate(result.businessDate)}</>
                  ) : null}
                  . Both entries are in the book.
                </>
              }
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} disabled={reverse.isPending}>
            {result === null ? 'Cancel' : 'Close'}
          </Button>
          {result === null ? (
            <Button
              variant="danger"
              disabled={trimmed === '' || reverse.isPending}
              onClick={() => {
                reverse.mutate(
                  { journalPublicId: entry.publicId, request: { reason: trimmed } },
                  {
                    onSuccess: (posted) => {
                      setResult(posted)
                      onPosted?.(posted.entryNumber)
                    },
                  },
                )
              }}
            >
              {reverse.isPending ? 'Working…' : 'Reverse the entry'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
